/**
 * GettingStartedStore
 *
 * Zustand store backing the "Getting Started" hub at /onboarding/get-started.
 * Tracks which checklist step keys the user has *initiated* (clicked at least
 * once). Persisted to localStorage so progress survives reloads and follows
 * the user across tabs in the same browser.
 *
 * Progress percent = initiated.length / TOTAL_STEPS (currently 6).
 *
 * Step keys (CANONICAL_STEPS):
 *   setup-firm, navigate, create-matter, intake-form, ai-scribe,
 *   notify-institutions
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type { OnboardingStepId } from '@/lib/services/onboarding/types';

export const CANONICAL_STEPS = [
  'setup-firm',
  'navigate',
  'create-matter',
  'intake-form',
  'ai-scribe',
  'notify-institutions'
] as const;

export type GettingStartedStepKey = (typeof CANONICAL_STEPS)[number];

export const TOTAL_STEPS = CANONICAL_STEPS.length;

/**
 * Step labels in canonical display order. `title` is the long form used
 * by the hub page; `shortTitle` is the compact label for navigation
 * surfaces (icon-rail tooltip etc.). Excludes `setup-firm` because its
 * label is role-aware — use `getSetupFirmStepLabel(isAdmin)` for that.
 */
export interface GettingStartedStepDescriptor {
  id: Exclude<GettingStartedStepKey, 'setup-firm'>;
  title: string;
  shortTitle: string;
  description: string;
}

export const GETTING_STARTED_CHECKLIST_STEPS: readonly GettingStartedStepDescriptor[] =
  [
    {
      id: 'navigate',
      title: 'Navigate Vale with our guided tour',
      shortTitle: 'Navigate Vale',
      description: 'A walkthrough of the navigation, key sections, and layout'
    },
    {
      id: 'create-matter',
      title: 'Create a matter',
      shortTitle: 'Create a matter',
      description: 'Create your first probate matter and learn the basics'
    },
    {
      id: 'intake-form',
      title: 'Send a client intake form',
      shortTitle: 'Send intake form',
      description:
        'Send an intake form to the client and import the returned data'
    },
    {
      id: 'ai-scribe',
      title: 'Try AI Scribe',
      shortTitle: 'Try AI Scribe',
      description:
        'Record or upload a client meeting and import AI-transcribed data'
    },
    {
      id: 'notify-institutions',
      title: 'Notify institutions',
      shortTitle: 'Notify institutions',
      description:
        'Send notifications to relevant institutions (banks, super funds, etc.)'
    }
  ] as const;

/**
 * Role-aware label for the setup-firm step. The step's real scope is
 * uploading letterhead + signatures (firm letterhead/email signature for
 * admins, plus the user's own handwritten signature) — NOT configuring the
 * firm entity itself, which already exists by the time a user reaches this
 * hub. The admin label therefore names the assets, not "set up your firm",
 * which wrongly implied an already-configured firm was unconfigured.
 */
export function getSetupFirmStepLabel(isAdmin: boolean): {
  title: string;
  shortTitle: string;
} {
  return isAdmin
    ? {
        title: 'Add your letterhead & signatures',
        shortTitle: 'Letterhead & signatures'
      }
    : { title: 'Set up your signature', shortTitle: 'Set up signature' };
}

interface GettingStartedState {
  /** Step keys the user has clicked into at least once. */
  initiated: GettingStartedStepKey[];
  /**
   * Cached profile-derived verdict for the role-aware "Setup firm" step.
   *   null  — never resolved this session/install
   *   true  — profile data confirmed all required setup is done
   *   false — profile data confirmed setup is still incomplete
   * Persisted so subsequent loads can render the row immediately
   * without waiting for the profile + onboarding-status fetches.
   */
  setupFirmComplete: boolean | null;
  /**
   * Onboarding wizard step ids the user has explicitly opted out of
   * (e.g. clicked "I don't sign documents" on the personal-signature
   * step). Persisted so a non-completion exit doesn't cause the
   * dismissed step to reappear on next sign-in.
   *
   * The backend's status payload only knows whether a step is
   * `completed`, not whether the user has chosen to skip it
   * permanently — this list is the frontend-only "user said no" signal.
   */
  dismissedOnboardingSteps: OnboardingStepId[];
  /**
   * Tracks whether persist middleware has rehydrated state from localStorage.
   * Lives in store state (not React state) so consumers subscribe via the
   * normal selector — no useEffect/setState dance, SSR-safe, no cascading
   * renders. Recommended Zustand pattern.
   * @see https://zustand.docs.pmnd.rs/integrations/persisting-store-data#how-can-i-check-if-my-store-has-been-hydrated
   */
  hasHydrated: boolean;
}

interface GettingStartedActions {
  /** Mark a step as initiated. No-op if already present. */
  markInitiated: (key: GettingStartedStepKey) => void;
  /** Cache the latest profile-derived setup-firm completion verdict. */
  setSetupFirmComplete: (value: boolean) => void;
  /** Mark an onboarding wizard step as user-dismissed. Idempotent. */
  dismissOnboardingStep: (id: OnboardingStepId) => void;
  /** Internal — flipped to true by `onRehydrateStorage`. */
  setHasHydrated: (value: boolean) => void;
  /** Clear all progress (useful for QA / settings reset). */
  reset: () => void;
}

type GettingStartedStore = GettingStartedState & GettingStartedActions;

const initialState: GettingStartedState = {
  initiated: [],
  setupFirmComplete: null,
  dismissedOnboardingSteps: [],
  hasHydrated: false
};

export const useGettingStartedStore = create<GettingStartedStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        markInitiated: (key) => {
          const { initiated } = get();
          if (initiated.includes(key)) return;
          set({ initiated: [...initiated, key] }, false, 'markInitiated');
        },

        setSetupFirmComplete: (value) => {
          if (get().setupFirmComplete === value) return;
          set({ setupFirmComplete: value }, false, 'setSetupFirmComplete');
        },

        dismissOnboardingStep: (id) => {
          const { dismissedOnboardingSteps } = get();
          if (dismissedOnboardingSteps.includes(id)) return;
          set(
            {
              dismissedOnboardingSteps: [...dismissedOnboardingSteps, id]
            },
            false,
            'dismissOnboardingStep'
          );
        },

        setHasHydrated: (value) => {
          set({ hasHydrated: value }, false, 'setHasHydrated');
        },

        reset: () => {
          // Preserve hasHydrated across resets — the store is still hydrated.
          set(
            { ...initialState, hasHydrated: get().hasHydrated },
            false,
            'reset'
          );
        }
      }),
      {
        name: 'vale_get_started_progress',
        storage: createJSONStorage(() => localStorage),
        // Only persist user data — never persist hasHydrated (must start
        // false on every fresh page load).
        partialize: (state) => ({
          initiated: state.initiated,
          setupFirmComplete: state.setupFirmComplete,
          dismissedOnboardingSteps: state.dismissedOnboardingSteps
        }),
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        }
      }
    ),
    {
      name: 'GettingStartedStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

// ─── Selector hooks ───────────────────────────────────────────────────

/** All initiated step keys. */
export const useGettingStartedInitiated = () =>
  useGettingStartedStore((s) => s.initiated);

/** Whether a specific step has been initiated. */
export const useGettingStartedIsInitiated = (key: GettingStartedStepKey) =>
  useGettingStartedStore((s) => s.initiated.includes(key));

/** Cached profile-derived setup-firm completion (null until first resolve). */
export const useGettingStartedSetupFirmComplete = () =>
  useGettingStartedStore((s) => s.setupFirmComplete);

/** Onboarding wizard step ids the user has dismissed via "don't show again". */
export const useDismissedOnboardingSteps = () =>
  useGettingStartedStore((s) => s.dismissedOnboardingSteps);

/**
 * Tracks whether the persist middleware has finished hydrating the store
 * from localStorage. Returns false on first render (server + initial
 * client paint), then true once `onRehydrateStorage` flips the in-store
 * flag. Components that branch on `initiated` should treat the
 * pre-hydration state as loading to avoid the "default to not-checked"
 * flicker.
 */
export const useGettingStartedHydrated = (): boolean =>
  useGettingStartedStore((s) => s.hasHydrated);

// ─── Non-hook accessor (for use outside React components) ─────────────

export const getGettingStartedActions = () => ({
  markInitiated: useGettingStartedStore.getState().markInitiated,
  setSetupFirmComplete: useGettingStartedStore.getState().setSetupFirmComplete,
  dismissOnboardingStep:
    useGettingStartedStore.getState().dismissOnboardingStep,
  reset: useGettingStartedStore.getState().reset
});
